import React from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Play, X, CheckCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface PausedRowContentProps {
  noteText?: string;
  startedAt?: string;
  pausedAt?: string;
  progress?: number; // 0-100 percentage
  currentPage?: number;
  totalPages?: number;
  tags?: string[];
  onResumeReading?: () => void;
  onMarkAsRead?: () => void;
  onMarkAsUnfinished?: () => void;
  loading?: boolean;
}

function relativeTime(dateString: string): string {
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  } catch {
    return 'some time ago';
  }
}

export function PausedRowContent({
  noteText,
  startedAt,
  pausedAt,
  progress = 0,
  currentPage,
  totalPages,
  tags,
  onResumeReading,
  onMarkAsRead,
  onMarkAsUnfinished,
  loading = false
}: PausedRowContentProps) {
  const hasPageTracking = currentPage !== undefined && totalPages !== undefined && totalPages > 0;
  
  return (
    <>
      {/* Status and metadata row */}
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800 border-amber-300">
          Paused
        </Badge>
        
        {pausedAt && (
          <span className="ml-auto text-xs text-muted-foreground flex-shrink-0">
            Paused {relativeTime(pausedAt)}
          </span>
        )}
      </div>

      {/* Progress bar - shows where they left off */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Progress when paused</span>
          {hasPageTracking ? (
            <span className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages} ({progress}%)
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">{progress}%</span>
          )}
        </div>
        <Progress value={progress} className="h-2 opacity-60" />
        {startedAt && (
          <p className="text-xs text-muted-foreground">
            Started {relativeTime(startedAt)}
          </p>
        )}
      </div>

      {/* Tags */}
      {tags && tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {tags.map(tag => (
            <Badge key={tag} variant="outline" className="text-xs px-1.5 py-0">
              {tag}
            </Badge>
          ))}
        </div>
      )}

      {/* Notes */}
      {noteText ? (
        <div className="text-sm sm:text-base leading-relaxed whitespace-pre-line break-words">
          {noteText}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground italic">
          No reading notes.
        </p>
      )}

      {/* Action buttons */}
      {(onResumeReading || onMarkAsRead || onMarkAsUnfinished) && (
        <TooltipProvider>
          <div className="flex flex-col gap-2 mt-2">
            <div className="flex gap-2">
              {onResumeReading && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="default"
                      onClick={onResumeReading}
                      disabled={loading}
                      className="flex-1 gap-2"
                      aria-label="Resume Reading"
                    >
                      <Play className="w-4 h-4 sm:w-3 sm:h-3 flex-shrink-0" />
                      <span className="hidden sm:inline">Resume Reading</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="sm:hidden">
                    <p>Resume Reading</p>
                  </TooltipContent>
                </Tooltip>
              )}
              
              {onMarkAsRead && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={onMarkAsRead}
                      disabled={loading}
                      className="flex-1"
                      aria-label="Mark as Read"
                    >
                      <CheckCircle className="w-4 h-4 sm:w-3 sm:h-3 sm:mr-1 flex-shrink-0" />
                      <span className="hidden sm:inline">Mark as Read</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="sm:hidden">
                    <p>Mark as Read</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>

            {onMarkAsUnfinished && (
              <Button
                size="sm"
                variant="ghost"
                onClick={onMarkAsUnfinished}
                disabled={loading}
                className="w-full text-muted-foreground hover:text-orange-600"
              >
                <X className="w-3 h-3 mr-1" />
                <span className="text-sm">Mark as Unfinished</span>
              </Button>
            )}
          </div>
        </TooltipProvider>
      )}
    </>
  );
}

export default PausedRowContent;
